import React, { useEffect, useState } from 'react';
import { Dialog, DialogContentDrawerRenderProps } from '@economic/taco';
import { Button, Group, Switch } from '@economic/taco';

import { Field } from '../../../../../../shared/components/Form';
import { t, tBB } from '../../../../../../shared/components/l10n/l10n';
import { InvoicingMessageType, SendByType, BookAndSendFormData, InvoicingMessage } from '../types';
import { DisplayMessages } from '../../../shared/components/DisplayMessages';
import { BookOnlyConfirmationDialogContent } from '../BookOnlyConfirmationDialogContent';
import { useEmailOptionsPdfSelected, useEmailOptionsOioSelected, useEmailOptionsRecipientsSelected } from '../../utils';
import { EmailTemplatePicker } from '../../utils/EmailTemplatePicker';
import { SendAsEmail } from '../../../shared/components/SendBy';

function useRequiredFieldsValidation(): InvoicingMessage[] {
    const requiredFieldsMessages: InvoicingMessage[] = [];
    // check if email options make sense for sending order.
    const isPdfSelected = useEmailOptionsPdfSelected();
    const isOioSelected = useEmailOptionsOioSelected();
    // check if at least one recipient fields is entered
    const isRecipientSelected = useEmailOptionsRecipientsSelected();

    if (!isPdfSelected && !isOioSelected) {
        requiredFieldsMessages.push({ message: t`FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR`, type: InvoicingMessageType.Error });
    }

    if (!isRecipientSelected) {
        requiredFieldsMessages.push({
            message: t`FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR`,
            type: InvoicingMessageType.Error,
        });
    }

    return requiredFieldsMessages;
}

export function BookDraftEmailInvoiceDialogContent({
    drawer,
    information,
    formAction: onBookAndSendAction,
    onToggleMoneyflow,
    onConvertToEInvoice,
}: {
    drawer: DialogContentDrawerRenderProps;
    information: BookAndSendFormData;
    formAction: (sendByType: SendByType) => void;
    onCancel?: () => void;
    onToggleMoneyflow: () => void;
    onConvertToEInvoice: () => void;
}) {
    function bookOnlyAction() {
        onBookAndSendAction(SendByType.undefined);
    }

    function onBookOnlyAction() {
        if (information.skipBookOnlyConfirmation) {
            bookOnlyAction();
        } else {
            if (drawer.open) {
                drawer.toggle();
            }
            setBookOnlyConfirmation(true);
        }
    }

    const [showBookOnlyConfirmation, setBookOnlyConfirmation] = useState<boolean>(false);
    const requiredFieldValidationMessages = useRequiredFieldsValidation();
    const messages = [...(information.messages || []), ...requiredFieldValidationMessages];
    const isMoneyflowOfferToggled = information.moneyflowOffer?.toggled;
    const headerText = isMoneyflowOfferToggled
        ? t(`FORM_BOOKANDSEND_INVOICE_MONEYFLOW_HEADER`)
        : t(`FORM_BOOKANDSEND_INVOICE_HEADER`, {
              DOCUMENT_TYPE: information.isCreditNote
                  ? t`FORM_BOOKANDSEND_DOCUMENT_TYPE_CREDIT_NOTE`
                  : t`FORM_BOOKANDSEND_DOCUMENT_TYPE_DRAFT_INVOICE`,
          });
    const bookingTypeText = isMoneyflowOfferToggled
        ? t`FORM_BOOKANDSEND_MONEYFLOW_ENABLED_TEXT`
        : t`FORM_BOOKANDSEND_EMAIL_SINGLE_INVOICE_TEXT`;
    const bookingTypeExtraText = isMoneyflowOfferToggled && tBB(`FORM_BOOKANDSEND_MONEYFLOW_ENABLED_INFO`);
    const secondaryButton = isMoneyflowOfferToggled ? null : (
        <Button type="submit" onClick={() => onBookOnlyAction()}>{t`FORM_BOOKANDSEND_BOOK_TEXT`}</Button>
    );

    useEffect(() => {
        if (!showBookOnlyConfirmation && !drawer.open) {
            drawer.toggle();
        }
    });

    if (showBookOnlyConfirmation) {
        return (
            <BookOnlyConfirmationDialogContent
                onBack={() => setBookOnlyConfirmation(false)}
                onOk={bookOnlyAction}
                text={t`FORM_BOOKANDSEND_BOOK_ONLY_CONFIRMATION_TEXT`}
            />
        );
    }

    return (
        <div className="h-full flex flex-col">
            <Dialog.Title>{headerText}</Dialog.Title>

            <SendAsEmail title={bookingTypeText} text={bookingTypeExtraText && <em>{bookingTypeExtraText}</em>} />

            {information.canSendElectronically && (
                <Button className="self-start ml-6 mb-2" appearance="discrete" onClick={onConvertToEInvoice}>
                    {t`FORM_BOOKANDSEND_CONVERT_TO_EINVOICE`}
                </Button>
            )}

            <EmailTemplatePicker templates={information.templates} />

            <div className="book-and-send-dialog__messages">
                <DisplayMessages messages={messages} />
            </div>
            <div className="book-and-send-dialog__options">
                {information.canIncludePaymentLink && (
                    <Field.Checkbox
                        name="includePaymentLink"
                        text={t`FORM_BOOKANDSEND_EMAIL_INCLUDE_PAYMENT_LINK`}
                        fieldClassName="field--no-height"
                    />
                )}
                <Field.Checkbox
                    name="emailOptions.showPdf"
                    text={t`FORM_BOOKANDSEND_SHOW_INVOICE`}
                    fieldClassName="field--no-height"
                />
                {information.canToggleMoneyflow && (
                    <div className="field field--no-height">
                        <Switch
                            label={t`FORM_BOOKANDSEND_MONEYFLOW_TOGGLE`}
                            checked={!!information.moneyflowOffer?.toggled}
                            onChange={() => onToggleMoneyflow()}
                        />
                    </div>
                )}
            </div>
            <Field.Hidden name="sendByType" value={Number(SendByType.undefined)} />

            <Dialog.Footer>
                <Group>
                    {secondaryButton}

                    <Button
                        appearance="primary"
                        type="submit"
                        onClick={() => onBookAndSendAction(SendByType.email)}
                        disabled={!!requiredFieldValidationMessages.length}
                        autoFocus>
                        {t`FORM_BOOKANDSEND_SEND_TEXT`}
                    </Button>
                </Group>
            </Dialog.Footer>
        </div>
    );
}
